import { NoSymbolIcon } from "@heroicons/react/24/outline"
import React, { useContext, useState, useMemo } from "react"
import { Message, Icon } from "semantic-ui-react"
import createMarkup from "../../utils/createMarkup"
import BookerButton from "../ui/BookerButton"
import { BookerContext } from "../../contexts/BookerContext"
import { CampingStyleProps } from "../../interfaces"
import CheckboxWithLabel from "../ui/CheckboxWithLabelForForm"
import { formatPhoneNumber } from "~/utils"

interface IComponentProps {
  disabledPurchase
  selectedCampingStyle
  errorMessage
  isBlocked
  handleSubmit
}

const PurchaseBottomBar = ({
  selectedCampingStyle,
  disabledPurchase,
  errorMessage,
  isBlocked,
  handleSubmit,
}: IComponentProps) => {
  const { camp, secondaryColor } = useContext(BookerContext)
  const campingStyle: CampingStyleProps = selectedCampingStyle

  const [isLegalAcknowledgement, setIsLegalAcknowledgement] = useState<Record<string, boolean>>(() => {
    if (!campingStyle.legal_acknowledgements?.length) return {}

    return campingStyle.legal_acknowledgements.reduce(
      (acc, _, index) => {
        acc[index] = false
        return acc
      },
      {} as Record<string, boolean>,
    )
  })

  const isLegalAcknowledgementDisabled = useMemo(() => {
    if (!campingStyle.legal_acknowledgements?.length) {
      return false
    }

    const acknowledgementsCount = campingStyle.legal_acknowledgements.length
    const checkedAcknowledgements = Object.values(isLegalAcknowledgement).filter((val) => val === true).length

    return checkedAcknowledgements < acknowledgementsCount
  }, [isLegalAcknowledgement, campingStyle.legal_acknowledgements])

  return (
    <>
      {campingStyle.refund_policy && (
        <Message info size="small" className="whitespace-pre-wrap flex items-start">
          <Icon name="info circle" />
          <div dangerouslySetInnerHTML={createMarkup(campingStyle.refund_policy)}></div>
        </Message>
      )}

      {errorMessage?.length ? <Message error content={errorMessage} /> : <></>}

      {campingStyle.legal_acknowledgements && (
        <>
          <div className="pt-2.5" />
          {campingStyle.legal_acknowledgements.map((acknowledgement, id) => {
            return (
              <div key={`legal-ack-${id}`} className="relative flex items-start py-2">
                <CheckboxWithLabel
                  label={
                    <label
                      htmlFor={`legal_acknowledgement_${id}`}
                      dangerouslySetInnerHTML={createMarkup(acknowledgement)}
                      className="text-gray-600"
                    ></label>
                  }
                  value={isLegalAcknowledgement[id]}
                  aria-describedby="comments-description"
                  name={`legal_acknowledgement_${id}`}
                  setValue={(checked) => {
                    setIsLegalAcknowledgement((prev) => ({
                      ...prev,
                      [id]: checked,
                    }))
                  }}
                  labelClasses="text-md my-2"
                  data-testid="legal-acknowledgement"
                />
              </div>
            )
          })}
        </>
      )}

      <div className="">
        <BookerButton
          disabled={disabledPurchase || isLegalAcknowledgementDisabled}
          onClick={handleSubmit}
          className={"relative mt-4 mb-1 flex"}
          secondaryColor={secondaryColor}
          buttonText={"Purchase Reservation"}
          minWidth={"100%"}
          componentWidth={"100%"}
          dataTestId="purchase-reservation"
        />

        <div className="mt-2.5 mb-8 text-sm">
          By submitting this form, I agree to{" "}
          {camp.rules && (
            <>
              the{" "}
              <a
                href={`/camps/${camp.slug}/rules`}
                target="_blank"
                className="text-blue-500 hover:text-blue-400 underline hover:underline"
              >
                Rules at {camp.name}
              </a>{" "}
              and{" "}
            </>
          )}
          authorize this payment to the campground via{" "}
          <a
            href="https://stripe.com/checkout/legal"
            target="_blank"
            className="text-blue-500 hover:text-blue-400 underline hover:underline"
          >
            Stripe
          </a>
          .
        </div>
        {isBlocked && (
          <div className="fixed top-0 left-0 flex items-center justify-center w-full h-full bg-gray-600 bg-opacity-95 z-50">
            <div className="text-center">
              <NoSymbolIcon className="w-14 h-14 text-white mx-auto mb-4" />
              {camp.custom_blocked_message ? (
                <p className="text-white text-lg font-medium max-w-xl mx-4 pb-2">{camp.custom_blocked_message}</p>
              ) : (
                <>
                  <p className="text-white text-lg font-medium max-w-xl mx-4 pb-2">
                    Due to prior violations of our{" "}
                    <a
                      href={`/camps/${camp.slug}/rules`}
                      target="_blank"
                      className="text-blue-400 hover:text-blue-500 underline"
                    >
                      Campground Rules
                    </a>
                    , we regret to inform you that you are no longer permitted to stay at {camp.name}.
                  </p>
                  <p className="text-white text-lg font-medium max-w-xl mx-4 pb-2">
                    If you believe this decision was made in error, please feel free to contact our office at{" "}
                    <a className="text-blue-400 hover:text-blue-500 underline" href={`tel:${camp.phone}`}>
                      {formatPhoneNumber(camp.phone)}
                    </a>{" "}
                    to discuss the matter.
                  </p>
                  <p className="text-white text-lg font-medium max-w-xl mx-4 pb-2">Thank you for your understanding.</p>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default PurchaseBottomBar
